import { ReactNode, useCallback, useMemo, useState } from "react";
import { DndContext, closestCenter, DragEndEvent, useSensor, PointerSensor, KeyboardSensor, useSensors, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { ListItem } from "./ListItem";
import { DataSource } from "../DataSource";
import { DataId } from "../DataId";
import { OrderableListChangeHandler } from "./OrderableListChangeHandler";
import { DataRow } from "../DataRow";
import styled from "styled-components";
import { ListItemOverlay } from "./ListItemOverlay";
import { LoadingOverlay } from "./LoadingOverlay";

/** The type of data source accepted by the `OrderableList` component. */

export interface OrderableListDataSource<TRow extends object> extends
  DataSource<TRow> { }

/** The `OrderableList` component properties. */
export interface OrderableListProps<TRow extends object> extends BaseProps {

  /** The list data source containing the items in the order they should be displayed. */
  readonly dataSource: OrderableListDataSource<TRow>;

  /** Specifies the template used to render specific items. */
  readonly children: (value: TRow, id: DataId) => ReactNode;

  /** Called when the list order is being changed. */
  readonly onChange?: OrderableListChangeHandler<TRow>;
}

const StyledOrderedList = styled.ol`
  padding: 0;
  margin: 0;
`;

const StyledRoot = styled.div`
  position: relative;

  &.loading>.viewport>.items {
    opacity: 0.5;
  }
`;

const ScrollViewport = styled.div`
  overflow: auto;
  max-height: 100%;
`;

/** A component which displays a list of items which can be reordered through dragging. */
export const OrderableList = <TRow extends object>(props: OrderableListProps<TRow>) => {
  const { children, dataSource, onChange } = props;

  const [draggingId, setDraggingId] = useState<DataId | null>(null);
  const [changing, setChanging] = useState(false);
  const [transitionalRows, setTransitionalRows] = useState<ReadonlyArray<DataRow<TRow>> | null>(null);

  const loading = (changing || dataSource.status === "initializing");

  const htmlProps = getHtmlPropsFromBaseProps(props, {
    className: ["orderable-list", { loading }],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 2, delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const dragEndHandler = useCallback(async (ev: DragEndEvent) => {
    const { active, over } = ev;

    if (!over) return;
    if (active.id === over.id) return;

    const { rows } = dataSource;

    const oldIndex = rows.findIndex((row) => row.id.toString() === active.id);
    const newIndex = rows.findIndex((row) => row.id.toString() === over.id);

    const newRows = arrayMove(rows as Array<DataRow<TRow>>, oldIndex, newIndex);

    try {
      setChanging(true);
      setTransitionalRows(newRows);
      if (await onChange?.(newRows, rows) ?? true)
        dataSource.reload();
    } finally {
      setTransitionalRows(null);
      setChanging(false);
      setDraggingId(null);
    }
  }, [onChange, dataSource.rows]);

  const dragStartHandler = useCallback((ev: DragStartEvent) => {
    const { active } = ev;
    setDraggingId(active.id);
  }, []);

  const draggingRow = useMemo(() => {
    return dataSource.rows.find((row) => row.id.toString() === draggingId);
  }, [draggingId, dataSource.rows]);

  const displayRows = (transitionalRows !== null) ? transitionalRows : dataSource.rows;

  const ids = useMemo(() => {
    return displayRows.map((row) => row.id.toString());
  }, [displayRows]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={dragStartHandler}
      onDragEnd={dragEndHandler}>
      <SortableContext items={ids}>
        <StyledRoot {...htmlProps}>
          <ScrollViewport className="viewport">
            <StyledOrderedList className="items">
              {displayRows.map((row) => (
                <ListItem key={row.id} id={row.id}>
                  {children(row.data, row.id)}
                </ListItem>
              ))}
            </StyledOrderedList>
          </ScrollViewport>
          {loading && <LoadingOverlay aria-busy={loading} />}
        </StyledRoot>
        <DragOverlay dropAnimation={null}>
          <ListItemOverlay>
            {draggingRow && children(draggingRow.data, draggingRow.id)}
          </ListItemOverlay>
        </DragOverlay>
      </SortableContext>
    </DndContext>
  );
};
